<template>
  <div
    class="w-full max-w-md md:w-[400px] flex flex-col gap-5 pt-5 px-6 pb-6 rounded-2xl border border-neutral-alpha-100"
    :class="{ 'bg-neutral-alpha-25': product.isPopular }"
  >
    <div class="flex flex-col gap-1">
      <BaseText
        type="label"
        size="md"
        class="text-white"
      >
        {{ product.tier }}
      </BaseText>
      <BaseText
        type="body"
        size="sm"
        class="text-neutral-alpha-650"
      >
        {{ product.description }}
      </BaseText>
    </div>

    <div class="h-px w-full bg-neutral-alpha-200" />

    <div class="flex flex-col gap-5">
      <div>
        <div class="flex items-end gap-2">
          <BaseText
            type="display"
            size="2xs"
            weight="600"
            class="text-white"
          >
            ${{ finalPrice }}
          </BaseText>
          <div class="py-1">
            <BaseText
              type="body"
              size="sm"
              class="text-neutral-alpha-650"
            >
              /month
            </BaseText>
          </div>
        </div>
        <div
          v-if="!isMonthly"
          class="flex items-center gap-1 mt-1 fade-in"
        >
          <MoneyCircleIcon class="money-icon flex-shrink-0" />
          <BaseText
            type="label"
            size="sm"
            class="text-white"
          >
            Save ${{ product.annualSavings }} annually
          </BaseText>
        </div>
      </div>
    </div>
    <!-- CTA -->
    <button
      class="w-full h-10 flex items-center justify-center transition-colors text-white rounded-lg p-2"
      :class="product.isPopular ? 'popular-cta' : 'regular-cta'"
      @click="handleAction(product)"
    >
      <BaseText
        type="heading"
        size="sm"
        class="whitespace-nowrap"
      >
        Start 7-Day Free Trial
      </BaseText>
    </button>

    <div class="h-px w-full bg-neutral-alpha-200" />

    <PlanFeatureList
      :plan-key="product.key"
      :is-monthly="isMonthly"
      :base-lens="product.baseLens"
    >
      <template #header>
        <div class="flex flex-col gap-1">
          <BaseText
            type="label"
            size="sm"
            class="text-white"
          >
            {{ product.baseUserCount }} User{{ product.baseUserCount > 1 ? 's' : '' }}
          </BaseText>
          <BaseText
            type="body"
            size="sm"
            class="text-neutral-alpha-650"
          >
            $20 per additional user
          </BaseText>
        </div>
      </template>
    </PlanFeatureList>

    <template v-if="showIntegrations">
      <div class="h-px w-full bg-neutral-alpha-200" />
      <div class="flex flex-col items-center gap-4 w-full">
        <BaseText
          type="body"
          size="sm"
          class="text-neutral-alpha-650"
        >
          Integrates with
        </BaseText>
        <div class="flex items-center justify-between w-full">
          <div
            v-for="integration in INTEGRATIONS"
            :key="integration.key"
            class="flex items-center"
          >
            <img
              :src="integration.icon"
              :alt="integration.label"
              class="w-5 h-5"
              :class="{ 'grok-icon': integration.key === 'grok' }"
            >
            <div class="px-1">
              <BaseText
                type="label"
                size="sm"
                class="text-white whitespace-nowrap"
              >
                {{ integration.label }}
              </BaseText>
            </div>
          </div>
        </div>
        <BaseText
          type="body"
          size="sm"
          class="text-neutral-alpha-650"
        >
          and more...
        </BaseText>
      </div>
    </template>
  </div>
</template>

<script>
import MoneyCircleIcon from '@/components/globals/Icons/MoneyCircleIcon.vue'
import PlanFeatureList from '@/components/account/subscription/PlanFeatureList.vue'

export default {
  name: 'DarkProductCard',
  components: {
    MoneyCircleIcon,
    PlanFeatureList
  },
  props: {
    product: {
      type: Object,
      required: true
    },
    isMonthly: {
      type: Boolean,
      default: false
    },
    allProducts: {
      type: Array,
      default: () => []
    },
    rounding: {
      type: String,
      default: 'all'
    }
  },
  data () {
    return {
      INTEGRATIONS: [
        { key: 'claude', label: 'Claude', icon: require('@/assets/icons/mcp-platforms/claude.svg') },
        { key: 'grok', label: 'Grok', icon: require('@/assets/icons/mcp-platforms/grok.svg') },
        { key: 'gemini', label: 'Gemini', icon: require('@/assets/icons/mcp-platforms/gemini.svg') },
        { key: 'chatgpt', label: 'ChatGPT', icon: require('@/assets/icons/mcp-platforms/chatgpt.svg') }
      ]
    }
  },
  computed: {
    isSaleActive () {
      return this.isMonthly && this.showDiscount
    },
    intervalType () {
      return this.isMonthly ? 'monthly' : 'annual'
    },
    finalPrice () {
      const prodPrice = this.product.price?.[this.intervalType]
      return this.isSaleActive && this.product.key !== 'basic-fp-plan' ? (prodPrice / 2).toFixed(0) : prodPrice
    },
    showIntegrations () {
      return this.product.key === 'workflow_plan' || this.product.key === 'agency_plan'
    }
  },
  methods: {
    handleAction (product) {
      const payload = {
        key: product.key,
        name: product.tier,
        description: product.description,
        price: this.finalPrice,
        interval: this.isMonthly ? 'month' : 'year',
        savingsPrice: product.annualSavings,
        includedUsers: product.baseUserCount,
        spyderCount: product.baseSpyder,
        lensCount: product.baseLens,
        includedProducts: product.includedProducts,
        priceId: product.priceId,
        addOn: product.addOns
      }
      this.$emit('action', payload)
    }
  }
}
</script>

<style scoped>
.money-icon {
  color: #EBBE7A
}

.grok-icon {
  filter: invert(1);
}

.fade-in {
  animation: fadeIn 100ms ease-in;
}
@keyframes fadeIn {
  from { opacity: 0.25 }
  to { opacity: 1 }
}

.popular-cta {
  background-color: #1F69FF;
  border: 1px solid rgba(255, 255, 255, 0.16);
}
.popular-cta:hover {
  background-color: #1A5CE6;
}

.regular-cta {
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.16);
}
.regular-cta:hover {
  border-color: rgba(255, 255, 255, 0.28);
}
</style>
